import React, { useCallback } from 'react'
import { graphql, compose } from 'react-apollo'
import {
  Col,
  Card,
  CardTitle,
  CardBody,
  Row,
  Button,
  Badge,
  Popover,
  PopoverHeader,
  PopoverBody,
  Modal,
  ModalHeader,
  ModalBody,
  NavItem,
  NavLink
} from 'reactstrap'
import moment from 'moment'
import Router from 'next/router'
import { checkScope } from '~/lib/helpers/checkScope'
import { dataUnready } from '~/lib/helpers/dataUnready'
import { pathToHrefAs } from '~/lib/helpers/pathToHrefAs'
import { ALERT_POLLING_INTERVAL } from '~/lib/constants/polling'
import { QueryAlerts, QueryMyScpAlerts, QueryEpfAlerts, UpdateAlert } from '~/queries/alert.gql'
import CustomAlerts from '~/components/shared/CustomAlerts'
import MyScpAlerts from '~/components/shared/MyScpAlerts'
import EpfAlerts from '~/components/shared/EpfAlerts'
import PropTypes from 'prop-types'
import useToggle from '~/hooks/useToggle'

function NotificationsBadge ({ count }) {
  let alertsCount = null

  if (count > 0) {
    alertsCount = <Badge className='rounded-circle' color='danger'>{count}</Badge>
  }

  return alertsCount
}

NotificationsBadge.propTypes = {
  count: PropTypes.number.isRequired
}

function NavNotification ({ title, handleClick, fromNow }) {
  return (
    <Row className='h-100 border-top py-3 mr-0 align-items-center'>
      <Col>
        <Button color='link' className='text-left font-weight-semibold' onClick={handleClick}>
          {title}
        </Button>
      </Col>
      <Col xs='auto' className='text-medium small'>{fromNow}</Col>
      <i className='fa fa-checkbox-blank-circle text-danger mr-2' />
    </Row>
  )
}

NavNotification.propTypes = {
  title: PropTypes.string,
  handleClick: PropTypes.func,
  fromNow: PropTypes.string
}

function NavNotifications ({ dataCustomAlerts, dataMyscpAlerts, dataEpfAlerts, totalAlerts, handleAlertClick }) {
  const [notificationsOpen, toggleNotifications] = useToggle()
  let results = <PopoverBody className='border-top'>No notifications</PopoverBody>

  // For popover "legacy" toggle, only close if not mobile view
  const toggleNotificationsPopover = useCallback(() => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      toggleNotifications()
    }
  }, [])

  // Toggle the notifications popover when handling alert click
  const handleAlertClickCustom = (url, alert) => {
    handleAlertClick(url, alert)

    setTimeout(() => {
      toggleNotifications()
    }, 10)
  }

  if (totalAlerts > 0) {
    results = (
      <PopoverBody className='p-0'>
        <CustomAlerts
          tag={NavNotification}
          data={dataCustomAlerts}
          handleAlertClick={handleAlertClickCustom}
        />
        <MyScpAlerts
          tag={NavNotification}
          data={dataMyscpAlerts}
          handleAlertClick={handleAlertClickCustom}
        />
        <EpfAlerts
          tag={NavNotification}
          data={dataEpfAlerts}
          handleAlertClick={handleAlertClickCustom}
        />
      </PopoverBody>
    )
  }

  return (
    <>
      <NavItem>
        <NavLink id='topnav-alerts-popover' onClick={toggleNotifications}>
          <i className='fa fa-bell-outline' />
          <NotificationsBadge count={totalAlerts} />
        </NavLink>
      </NavItem>
      <Popover
        className='popover-notifications p-0 d-none d-md-block'
        placement='bottom'
        trigger='legacy'
        delay={0}
        isOpen={notificationsOpen}
        target='topnav-alerts-popover'
        toggle={toggleNotificationsPopover}
        fade={false}
      >
        <PopoverHeader className='border-bottom-0'>Notifications</PopoverHeader>
        {results}
      </Popover>
      <Modal
        isOpen={notificationsOpen}
        toggle={toggleNotifications}
        modalClassName='d-md-none'
        backdropClassName='d-md-none'
        modalTransition={{ timeout: 0 }}
        backdropTransition={{ timeout: 0 }}
      >
        <ModalHeader tag='h2' close={<i className='close fa fa-close cursor-pointer' onClick={toggleNotifications} />}>
          Notifications
        </ModalHeader>
        <ModalBody className='p-0'>
          <div className='border-bottom'>
            {results}
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

function HomeNotification ({ title, handleClick, fromNow }) {
  return (
    <Card className='mb-2'>
      <CardBody className='p-3'>
        <Row className='align-items-center'>
          <Col>
            <Button color='link' className='text-left font-weight-semibold px-0' onClick={handleClick}>
              {title}
            </Button>
          </Col>
          <Col xs='auto' className='text-medium small'>{fromNow}</Col>
          <i className='fa fa-checkbox-blank-circle text-danger mr-1' />
        </Row>
      </CardBody>
    </Card>
  )
}

HomeNotification.propTypes = {
  title: PropTypes.string,
  handleClick: PropTypes.func,
  fromNow: PropTypes.string
}

function HomeNotifications ({ dataCustomAlerts, dataMyscpAlerts, dataEpfAlerts, totalAlerts, handleAlertClick }) {
  let results = <p className='pt-3 text-center'>No notifications</p>

  if (totalAlerts > 0) {
    results = (
      <div className='scroll-container'>
        <CustomAlerts
          tag={HomeNotification}
          data={dataCustomAlerts}
          handleAlertClick={handleAlertClick}
        />
        <MyScpAlerts
          tag={HomeNotification}
          data={dataMyscpAlerts}
          handleAlertClick={handleAlertClick}
        />
        <EpfAlerts
          tag={HomeNotification}
          data={dataEpfAlerts}
          handleAlertClick={handleAlertClick}
        />
      </div>
    )
  }

  return (
    <Col lg='4' className='mb-4 mb-lg-0'>
      <Card className='h-100'>
        <CardBody className='px-3 px-md-4'>
          <CardTitle tag='h4' className='border-bottom mb-3 pb-2'>
            NOTIFICATIONS
          </CardTitle>
          {results}
        </CardBody>
      </Card>
    </Col>
  )
}

function Notifications ({ home = false, dataCustomAlerts, dataMyscpAlerts, dataEpfAlerts, updateAlert }) {
  const { unready: unreadyCustomAlerts, alerts: customAlerts } = dataUnready(dataCustomAlerts, 'alerts')
  const { unready: unreadyMyscpAlerts, myscpUserMetadata: myscpAlerts } = dataUnready(dataMyscpAlerts, 'myscpUserMetadata')
  const { unready: unreadyEpfAlerts, contact: epfAlerts } = dataUnready(dataEpfAlerts, 'contact')
  const NotificationsTag = home ? HomeNotifications : NavNotifications
  let totalAlerts = 0

  // Handle clicking alert
  const handleAlertClick = useCallback(async (url, alert) => {
    const local = url.match(/^\//)

    // If not local, open immediately
    if (!local) {
      window.open(url)
    }

    // Clear the alert if needed
    if (alert) {
      await updateAlert(alert)
    }

    // If local, open after done
    if (local) {
      const { href, as } = pathToHrefAs(url)

      Router.push(href, as).then(() => window.scrollTo(0, 0))
    }
  }, [])

  // Get total alerts
  if (!unreadyCustomAlerts && customAlerts) {
    totalAlerts += customAlerts.length
  }
  if (!unreadyMyscpAlerts && myscpAlerts && myscpAlerts.unreadMessageCount) {
    totalAlerts += 1
  }
  if (!unreadyEpfAlerts && epfAlerts && epfAlerts.deficiencies.length) {
    totalAlerts += 1
  }

  return (
    <NotificationsTag
      dataCustomAlerts={dataCustomAlerts}
      dataMyscpAlerts={dataMyscpAlerts}
      dataEpfAlerts={dataEpfAlerts}
      totalAlerts={totalAlerts}
      handleAlertClick={handleAlertClick}
    />
  )
}

Notifications.propTypes = {
  home: PropTypes.bool,
  profile: PropTypes.object.isRequired,
  dataCustomAlerts: PropTypes.object.isRequired,
  dataMyscpAlerts: PropTypes.object,
  dataEpfAlerts: PropTypes.object,
  updateAlert: PropTypes.func.isRequired
}

const withUpdateAlert = graphql(UpdateAlert, {
  name: 'updateAlert',
  props: ({ updateAlert }) => ({
    updateAlert: alert => updateAlert({
      variables: {
        alertModelId: alert.alertModel.id,
        userId: 'me',
        path: alert.path,
        input: { clearedAt: moment.utc().format() }
      },
      update (cache) {
        const data = cache.readQuery({
          query: QueryAlerts,
          variables: {
            userId: 'me'
          }
        })

        // Remove current alert from data.alerts object in cache
        data.alerts = data.alerts.filter(e => e.id !== alert.id)

        cache.writeQuery({
          query: QueryAlerts,
          variables: {
            userId: 'me'
          },
          data
        })
      }
    })
  })
})

const withAlerts = graphql(QueryAlerts, {
  name: 'dataCustomAlerts',
  options: () => ({
    ssr: false,
    fetchPolicy: 'cache-and-network',
    pollInterval: ALERT_POLLING_INTERVAL,
    variables: {
      userId: 'me'
    }
  })
})

const withAlertsMySCP = graphql(QueryMyScpAlerts, {
  name: 'dataMyscpAlerts',
  skip: ({ profile }) => !checkScope(profile, 'myscp.data.view'),
  options: () => ({
    ssr: false,
    fetchPolicy: 'cache-and-network',
    pollInterval: ALERT_POLLING_INTERVAL,
    variables: {
      userId: 'me'
    }
  })
})

const withAlertsEpf = graphql(QueryEpfAlerts, {
  name: 'dataEpfAlerts',
  skip: ({ profile }) => !checkScope(profile, 'epf.deficiencies.view'),
  options: () => ({
    ssr: false,
    fetchPolicy: 'cache-and-network',
    pollInterval: ALERT_POLLING_INTERVAL,
    variables: {
      id: 'me'
    }
  })
})

export default compose(
  withAlerts,
  withAlertsMySCP,
  withUpdateAlert,
  withAlertsEpf
)(Notifications)
